<script>
  import { format } from 'svelte-i18n';
  import Prism from 'svelte-prismjs';
  import { Button, Card, CardBody } from '@sveltestrap/sveltestrap';

  import { Capability } from '@client/components/capability';
  import { Clipboard } from '@client/components/clipboard';
  import {
    CodeField,
    DisclaimersField,
    KeyValueField,
    ListField,
    MultiSelectField,
    ParamsField,
    PasswordField,
    SelectField,
    SwitchField,
    TextField
  } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';
  import { ShowMore } from '@client/components/show-more';
  import { UserNotifications } from '@client/components/user-notifications';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';
  import { Permission } from '@client/pages/manage/domain-settings';

  import { DomainService } from '@client/services/domain';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { ATTACHMENT_VIEWS, COMPLIANCE_TRACKING } from '@client/enums';
  import {
    PERSONA_DEPARTMENTS,
    PERSONA_EXCLUDED,
    PERSONA_EXECUTIVES,
    PERSONA_INFLUENCERS,
    PERSONA_ROLES,
    VCARD_FIELDS_DEFAULTS
  } from '@client/enums';

  import { isValidDomain, isValidEmail, isValidUrl } from '@common/utils/validation';

  import { fusionScript } from './fusion';

  export let settings = {};
  export let onUpdate = null;
  export let categories = [];

  let roles = PERSONA_ROLES.map((value) => ({
    label: value,
    value: value.toLowerCase(),
    checked: (settings?.personas?.roles || []).includes(value.toLowerCase())
  }));

  let departments = PERSONA_DEPARTMENTS.map((value) => ({
    label: value,
    value: value.toLowerCase(),
    checked: (settings?.personas?.departments || []).includes(value.toLowerCase())
  }));

  let executives = PERSONA_EXECUTIVES.map((value) => ({
    label: value,
    value: value.toLowerCase(),
    checked: (settings?.personas?.executives || []).includes(value.toLowerCase())
  }));

  let influencers = PERSONA_INFLUENCERS.map((value) => ({
    label: value,
    value: value.toLowerCase(),
    checked: (settings?.personas?.influencers || []).includes(value.toLowerCase())
  }));

  let excluded = PERSONA_EXCLUDED.map((value) => ({
    label: value,
    value: value.toLowerCase(),
    checked: (settings?.personas?.excluded || []).includes(value.toLowerCase())
  }));

  let selectedComplianceTrackingOptions = [];
  let sslCertStatusBadge = '';
  let sslCertStatusCode = '';
  let sslCertStatusInfo = '';
  let requestingCert = false;
  let requestingCertError = null;
  let fusionJS = '';
  let copyText = 'label.COPY';

  let complianceTrackingOptions = COMPLIANCE_TRACKING.map((item) => {
    item.checked = !!settings[item.value];
    item.label = $format(item.label);
    return item;
  });

  const onCopy = () => {
    copyText = 'label.COPIED';

    setTimeout(() => {
      copyText = 'label.COPY';
    }, 1200);
  };

  const requestSSLCertificate = async () => {
    $appStore.loading = true;

    requestingCert = true;
    requestingCertError = null;

    const response = await DomainService.requestSSLCertificate();

    $appStore.loading = false;

    if (response?.status === 500) {
      requestingCertError = $format('label.REQUEST_FOR_CERT_FAILED');
    } else {
      if (response?.code === 'CERT_REQUESTED') {
        sslCertStatusBadge = $format('label.SUCCESS.REQUEST_FOR_CERT');
      }
    }

    requestingCert = false;
  };

  const checkSSLCertificate = async () => {
    const response = await DomainService.checkSSLCertificate();

    if (response) {
      sslCertStatusInfo = response.message;
      sslCertStatusCode = response.code;

      if (sslCertStatusCode === 0) {
        sslCertStatusBadge = $format('label.REQUEST_FOR_CERT_INSTALLED', { values: { info: sslCertStatusInfo } });
      }

      if (sslCertStatusCode === 1) {
        sslCertStatusBadge = $format('label.REQUEST_FOR_CERT_CNAME_NOT_INSTALLED', {
          values: { info: sslCertStatusInfo }
        });
      }

      if (sslCertStatusCode === 2) {
        sslCertStatusBadge = $format('label.REQUEST_FOR_CERT_NO_CERT_DETECTED', {
          values: { info: sslCertStatusInfo }
        });
      }
    }
  };

  checkSSLCertificate();

  $: selectedComplianceTrackingOptions = complianceTrackingOptions.filter((item) => item?.checked);
  $: selectedRoles = roles.filter((item) => item?.checked && item.value);
  $: selectedDepartments = departments.filter((item) => item?.checked);
  $: selectedExecutives = executives.filter((item) => item?.checked);
  $: selectedInfluencers = influencers.filter((item) => item?.checked);
  $: selectedExcluded = excluded.filter((item) => item?.checked);
  $: fusionJS = fusionScript(settings.cname);
  $: allowed = categories.find((category) => category.id === 'general')?.items?.map((section) => section.id) || [];
  $: showActions = $userStore?.isStaff;
</script>

<div id="general">
  <Permission {allowed} id="general-domains">
    <ScrollableSection id="general-domains">
      <h1 class="mb-3">{$format('label.DOMAINS')}</h1>
      <Card>
        <CardBody class="p-4 pt-1">
          <TextField
            id="cname"
            label={$format('label.WHITE_LABEL_DOMAIN')}
            tooltip={$format('label.TOOLTIP.WHITE_LABEL_DOMAIN')}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_DOMAIN')}
            validation={isValidDomain}
            bind:value={settings.cname}
            {onUpdate}
            {showActions}
            allowCopy
          >
            <ShowMore collapsedText={$format('label.SHOW_ALL')} expandedText={$format('label.HIDE_ALL')}>
              <div class="cnames">
                <b>{$format('label.ALL_USED_WHITE_LABEL_DOMAINS')}</b>
                {#key settings.cnames}
                  <ul>
                    {#each settings.cnames as cname, index}
                      <li class:active={cname === settings.cname}>
                        {cname}
                      </li>
                    {/each}
                  </ul>
                {/key}
              </div>
            </ShowMore>
          </TextField>

          <ListField
            id="aliases"
            label={$format('label.DOMAIN_ALIASES')}
            tooltip={$format('label.TOOLTIP.DOMAIN_ALIASES')}
            addButtonLabel={$format('label.ADD_ALIAS')}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_DOMAIN')}
            validation={isValidDomain}
            bind:values={settings.aliases}
            {onUpdate}
            {showActions}
            allowCopy
            allowBulkAdd={true}
            allowBillingIdLookup={true}
          />

          <ListField
            id="internaldomains"
            label={$format('label.INTERNAL_DOMAINS')}
            tooltip={$format('label.TOOLTIP.INTERNAL_DOMAINS')}
            addButtonLabel={$format('label.ADD_DOMAIN')}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_DOMAIN')}
            validation={isValidDomain}
            bind:values={settings.internaldomains}
            {onUpdate}
            {showActions}
            allowCopy
            allowBulkAdd={true}
            allowBillingIdLookup={true}
          />

          <SwitchField
            id="cnamessl"
            label={$format('label.CNAME_TLS')}
            badge={sslCertStatusBadge}
            tooltip={$format('label.TOOLTIP.CNAME_TLS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.cnamessl}
            {onUpdate}
            {showActions}
          >
            {#if sslCertStatusCode === 2}
              <button
                class="btn btn-secondary btn-sm request-cert"
                on:click={requestSSLCertificate}
                disabled={requestingCert}
              >
                {#if requestingCert}
                  {$format('label.REQUESTING')}
                {:else}
                  {$format('label.REQUEST_CERTIFICATE')}
                {/if}
              </button>

              {#if requestingCertError}
                <p class="error">{requestingCertError}</p>
              {/if}
            {/if}
          </SwitchField>

          <SwitchField
            id="cnamehsts"
            label={$format('label.CNAME_HSTS')}
            tooltip={$format('label.TOOLTIP.CNAME_HSTS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.cnamehsts}
            {onUpdate}
            {showActions}
          />

          <TextField
            id="logosrc"
            label={$format('label.LOGO_URL')}
            tooltip={$format('label.TOOLTIP.LOGO_URL')}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
            validation={isValidUrl}
            bind:value={settings.logosrc}
            {onUpdate}
            {showActions}
            allowCopy
          >
            {#if settings.logosrc}
              <ShowMore collapsedText={$format('label.SHOW_PREVIEW')} expandedText={$format('label.HIDE_PREVIEW')}>
                <div class="image-preview">
                  <div class="bold">{$format('label.PREVIEW')}</div>
                  <img src={settings.logosrc} alt={$format('label.COMPANY_LOGO')} />
                </div>
              </ShowMore>
            {/if}
          </TextField>

          <TextField
            id="faviconsrc"
            label={$format('label.FAVICON_URL')}
            tooltip={$format('label.TOOLTIP.FAVICON_URL')}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
            validation={isValidUrl}
            bind:value={settings.faviconsrc}
            {onUpdate}
            {showActions}
            allowCopy
          >
            {#if settings.faviconsrc}
              <ShowMore collapsedText={$format('label.SHOW_PREVIEW')} expandedText={$format('label.HIDE_PREVIEW')}>
                <div class="image-preview">
                  <div class="bold">{$format('label.PREVIEW')}</div>
                  <img src={settings.faviconsrc} alt={$format('label.COMPANY_LOGO')} />
                </div>
              </ShowMore>
            {/if}
          </TextField>

          <TextField
            id="cnameredirect"
            label={$format('label.REDIRECT_URL')}
            tooltip={$format('label.TOOLTIP.REDIRECT_URL')}
            validation={isValidUrl}
            validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
            bind:value={settings.cnameredirect}
            allowCopy
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablecdn"
            label={$format('label.ENABLE_CDN_BETA')}
            tooltip={$format('label.TOOLTIP.ENABLE_CDN_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablecdn}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enableinlineimages"
            label={$format('label.AUTO_INLINE_IMAGE_BUCKET_URLS')}
            tooltip={$format('label.TOOLTIP.AUTO_INLINE_IMAGE_BUCKET_URLS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enableinlineimages}
            {onUpdate}
            {showActions}
          />

          <ListField
            id="allowedinlinedomains"
            label={$format('label.AUTO_INLINE_IMAGE_URLS')}
            tooltip={$format('label.TOOLTIP.AUTO_INLINE_IMAGE_URLS')}
            addButtonLabel={$format('label.ADD_PREFIX')}
            bind:values={settings.allowedinlinedomains}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enabledynamicgroups"
            label={$format('label.DYNAMIC_GROUPS_BETA')}
            tooltip={$format('label.TOOLTIP.DYNAMIC_GROUPS_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enabledynamicgroups}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="pgpenabled"
            label={$format('label.PGP_ENCRYPTION_CSV_SYNC')}
            tooltip={$format('label.TOOLTIP.PGP_ENCRYPTION_CSV_SYNC')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.pgpenabled}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="allowClone"
            label={$format('label.ALLOW_SETTINGS_CLONING')}
            tooltip={$format('label.TOOLTIP.ALLOW_SETTINGS_CLONING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.allowClone}
            {onUpdate}
            {showActions}
          />

          <Capability action="manage" subject="staff">
            <SwitchField
              id="disableservice"
              label="{$format('label.SUSPEND_SERVICE')} ({settings.domainname})"
              tooltip={$format('label.TOOLTIP.SUSPEND_SERVICE')}
              valueOn={$format(enabledValue)}
              valueOff={$format(disabledValue)}
              bind:checked={settings.disableservice}
              {onUpdate}
              {showActions}
            />

            <SwitchField
              id="enablenewui"
              label={$format('label.NEW_UI')}
              tooltip={$format('label.TOOLTIP.ENABLE_NEW_UI')}
              valueOn={$format(enabledValue)}
              valueOff={$format(disabledValue)}
              bind:checked={settings.enablenewui}
              {onUpdate}
              {showActions}
            />

            <SwitchField
              id="defaultnewui"
              label={$format('label.DEFAULT_TO_NEW_UI')}
              tooltip={$format('label.TOOLTIP.DEFAULT_TO_NEW_UI')}
              valueOn={$format(enabledValue)}
              valueOff={$format(disabledValue)}
              bind:checked={settings.defaultnewui}
              {onUpdate}
              {showActions}
            />
          </Capability>
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Permission {allowed} id="general-signatures">
    <ScrollableSection id="general-signatures">
      <h1 class="mb-3">{$format('label.SIGNATURES')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <TextField
            id="sigcode"
            label={$format('label.SIGNATURE_BLOCK_CODE')}
            tooltip={$format('label.TOOLTIP.SIGNATURE_BLOCK_CODE')}
            bind:value={settings.sigcode}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="signaturesonly"
            label={$format('label.SIGNATUERS_ONLY')}
            tooltip={$format('label.TOOLTIP.SIGNATURES_ONLY')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.signaturesonly}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablestamping"
            label={$format('label.SIGNATURE_STAMPING')}
            tooltip={$format('label.TOOLTIP.SIGNATURE_STAMPING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablestamping}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disableclearsigblocks"
            label={$format('label.QUOTED_SIGNATURE_BLOCKS')}
            tooltip={$format('label.TOOLTIP.QUOTED_SIGNATURE_BLOCKS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disableclearsigblocks}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="fullsignaturefirstreply"
            label={$format('label.FULL_SIGNATURE_ON_INITIAL_REPLY')}
            tooltip={$format('label.TOOLTIP.FULL_SIGNATURE_ON_INITIAL_REPLY')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.fullsignaturefirstreply}
            {onUpdate}
            {showActions}
          />

          <TextField
            id="tagreplacement"
            label={$format('label.TAG_REPLACEMENT')}
            tooltip={$format('label.TOOLTIP.TAG_REPLACEMENT')}
            bind:value={settings.tagreplacement}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="tagreplacementatbottom"
            label={$format('label.TAG_PLACEMENT')}
            tooltip={$format('label.TOOLTIP.TAG_PLACEMENT')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.tagreplacementatbottom}
            {onUpdate}
            {showActions}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Permission {allowed} id="general-banners">
    <ScrollableSection id="general-banners">
      <h1 class="mb-3">{$format('label.BANNERS')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <SwitchField
            id="bannersdefault"
            label={$format('label.DEFAULT_TO_BANNERS')}
            tooltip={$format('label.TOOLTIP.DEFAULT_TO_BANNERS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.bannersdefault}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablebannerstamping"
            label={$format('label.BANNER_STAMPING')}
            tooltip={$format('label.TOOLTIP.BANNER_STAMPING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablebannerstamping}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="billing-disablebanners"
            label={$format('label.DISABLE_BANNERS')}
            tooltip={$format('label.TOOLTIP.DISABLE_BANNERS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.billing.disablebanners}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enableinlineads"
            label={$format('label.INLINE_BANNERS')}
            tooltip={$format('label.TOOLTIP.INLINE_BANNERS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enableinlineads}
            {onUpdate}
            {showActions}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Permission {allowed} id="general-bizcards">
    <ScrollableSection id="general-bizcards">
      <h1 class="mb-3">{$format('label.BUSINESS_CARDS')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <SwitchField
            id="enablebizcards"
            label={$format('label.ENABLE_BUSINESS_CARDS')}
            tooltip={$format('label.TOOLTIP.ENABLE_BUSINESS_CARDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablebizcards}
            {onUpdate}
          />

          <SwitchField
            id="privatebizcards"
            label={$format('label.PRIVATE_BUSINESS_CARDS')}
            tooltip={$format('label.TOOLTIP.PRIVATE_BUSINESS_CARDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.privatebizcards}
            {onUpdate}
            {showActions}
          />
          <SwitchField
            id="enablevcards"
            label={$format('label.ENABLE_VCARDS')}
            tooltip={$format('label.TOOLTIP.ENABLE_VCARDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablevcards}
            {onUpdate}
            {showActions}
          />

          <KeyValueField
            id="vcardfieldmappings"
            label={$format('label.VCARD_FIELD_MAPPINGS')}
            tooltip={$format('label.TOOLTIP.VCARD_FIELD_MAPPINGS')}
            addButtonLabel={$format('label.ADD_FIELD')}
            keyColumnLabel={$format('label.FIELD')}
            keyProperty="field"
            valueColumnLabel={$format('label.VALUE')}
            bind:values={settings.vcardfieldmappings}
            {onUpdate}
            {showActions}
          >
            <span slot="action" class="field-mapping-actions" let:onExternalAdd>
              <Button size="sm" color="secondary" on:click={() => onExternalAdd([...VCARD_FIELDS_DEFAULTS])}>
                {$format('label.COPY_DEFAULT_VCARD_FIELDS')}
              </Button>
            </span>
          </KeyValueField>

          <!-- <CodeField
            id="bizcardtemplate"
            label={$format('label.BUSINESS_CARD_HTML_TEMPLATE')}
            tooltip={$format('label.TOOLTIP.BUSINESS_CARD_HTML_TEMPLATE')}
            bind:value={settings.bizcardtemplate}
            {onUpdate}
            {showActions}
          />

          <CodeField
            id="bizcardmanifesttemplate"
            label={$format('label.MANIFEST_JSON_TEMPLATE')}
            tooltip={$format('label.TOOLTIP.MANIFEST_JSON_TEMPLATE')}
            bind:value={settings.bizcardmanifesttemplate}
            {onUpdate}
            {showActions}
          /> -->
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Capability action="manage" subject="staff">
    <ScrollableSection id="general-billing">
      <h1 class="mb-3">{$format('label.BILLING')}</h1>
      <Card>
        <CardBody class="p-4 pt-1">
          <!-- <SelectField
            id="billingtype"
            label="Billing Settings"
            tooltip="This is your preferred billing type."
            bind:value={settings.billingtype}
            selected={BILLING_TYPES.find(({ value }) => value === settings.billingtype)}
            options={BILLING_TYPES}
          /> -->

          <TextField
            id="billing-warning"
            label={$format('label.BILLING_WARNING')}
            tooltip={$format('label.TOOLTIP.BILLING_WARNING')}
            bind:value={settings.billing.warning}
            {onUpdate}
            {showActions}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  </Capability>

  <Permission {allowed} id="general-email">
    <ScrollableSection id="general-email">
      <h1 class="mb-3">{$format('label.EMAIL_AND_TRACKING')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <SelectField
            id="attTemplateType"
            label={$format('label.ATTACHMENT_VIEW_DOWNLOAD_TEMPLATE')}
            tooltip={$format('label.TOOLTIP.ATTACHMENT_VIEW_DOWNLOAD_TEMPLATE')}
            selected={ATTACHMENT_VIEWS.find(({ value }) => value === (settings.attTemplateType || 'normal'))}
            options={ATTACHMENT_VIEWS}
            bind:value={settings.attTemplateType}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enableadvancedtemplates"
            label={$format('label.ENABLE_ADVANCED_TEMPLATING_ENGINE_BETA')}
            tooltip={$format('label.TOOLTIP.ENABLE_ADVANCED_TEMPLATING_ENGINE_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enableadvancedtemplates}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablefromblock"
            label={$format('label.ENABLE_FROM_BLOCK_OVERRIDES_BETA')}
            tooltip={$format('label.TOOLTIP.ENABLE_FROM_BLOCK_OVERRIDES_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablefromblock}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="usebodyfrom"
            label={$format('label.USE_EMAIL_BODY_FROM_HEADER')}
            tooltip={$format('label.TOOLTIP.USE_EMAIL_BODY_FROM_HEADER')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.usebodyfrom}
            {onUpdate}
            {showActions}
          />

          <TextField
            id="slideviewmaxseconds"
            label={$format('label.SLIDE_VIEW_MAX_TIME')}
            tooltip={$format('label.TOOLTIP.SLIDE_VIEW_MAX_TIME')}
            formatValue={(value) => (value !== null ? `${value} Seconds` : '-')}
            bind:value={settings.slideviewmaxseconds}
            {onUpdate}
            {showActions}
          />

          <TextField
            id="dropsize"
            label={$format('label.AUTO_TRACK_ATTACHMENT_X_MBYTES')}
            tooltip={$format('label.TOOLTIP.AUTO_TRACK_ATTACHMENT_X_MBYTES')}
            formatValue={(value) => (value !== null ? `${value} MB` : '-')}
            bind:value={settings.dropsize}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="docfusionenabled"
            label={$format('label.DOCUMENT_PLAYBACK_BETA')}
            tooltip={$format('label.TOOLTIP.DOCUMENT_PLAYBACK_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.docfusionenabled}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disableinternal"
            label={$format('label.DISABLE_INTERNAL_TRACKING')}
            tooltip={$format('label.TOOLTIP.DISABLE_INTERNAL_TRACKING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disableinternal}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="replytrackingenabled"
            label={$format('label.REPLY_TRACKING')}
            tooltip={$format('label.TOOLTIP.REPLY_TRACKING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.replytrackingenabled}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disablesplitsend"
            label={$format('label.DISABLE_MULTI_RECIPIENT_PROCESSING')}
            tooltip={$format('label.TOOLTIP.DISABLE_MULTI_RECIPIENT_PROCESSING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disablesplitsend}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablenonsales"
            label={$format('label.ENABLE_NON_SALES_USERS')}
            tooltip={$format('label.TOOLTIP.ENABLE_NON_SALES_USERS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablenonsales}
            {onUpdate}
            {showActions}
          />

          <MultiSelectField
            id="compliancetracking3p"
            label={$format('label.SIGNATURE_COMPLIANCE_TRACKING_BETA')}
            tooltip={$format('label.TOOLTIP.SIGNATURE_COMPLIANCE_TRACKING_BETA')}
            bind:selected={selectedComplianceTrackingOptions}
            bind:options={complianceTrackingOptions}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disablemetadata"
            label={$format('label.DISABLE_METADATA')}
            tooltip={$format('label.TOOLTIP.DISABLE_METADATA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disablemetadata}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="enablesurveys"
            label={$format('label.ENABLE_SURVEYS_FEATURE')}
            tooltip={$format('label.TOOLTIP.ENABLE_SURVEYS_FEATURE')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablesurveys}
            {onUpdate}
            {showActions}
          />

          <DisclaimersField
            id="disclaimers"
            label={$format('label.CLEAR_DUPLICATE_DISCLAIMERS')}
            tooltip={$format('label.TOOLTIP.CLEAR_DUPLICATE_DISCLAIMERS')}
            bind:html={settings.disclaimerhtml}
            bind:text={settings.disclaimertext}
            bind:clear={settings.cleardisclaimer}
            bind:regexp={settings.disclaimerenableregex}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="ignoresmime"
            label={$format('label.S_MIME_PROCESSING')}
            tooltip={$format('label.TOOLTIP.S_MIME_PROCESSING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            inverted
            bind:checked={settings.ignoresmime}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disablegmailellipsefeature"
            label={$format('label.GMAIL_AUTO_ELLIPSING')}
            tooltip={$format('label.TOOLTIP.GMAIL_AUTO_ELLIPSING')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disablegmailellipsefeature}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="websitetrackingenabled"
            label={$format('label.WEBSITE_TRACKING_BETA')}
            tooltip={$format('label.TOOLTIP.WEBSITE_TRACKING_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.websitetrackingenabled}
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="fusionenabled"
            label={$format('label.WEBSITE_SITE_PLAYBACK_BETA')}
            tooltip={$format('label.TOOLTIP.WEBSITE_SITE_PLAYBACK_BETA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.fusionenabled}
            {onUpdate}
            {showActions}
          >
            <ShowMore collapsedText={$format('label.SHOW_SNIPPET')} expandedText={$format('label.HIDE_SNIPPET')}>
              <div class="snippet">
                <Clipboard text={fusionJS} let:copy on:copy={onCopy}>
                  <button on:click={copy} class="copy-to-clipboard">{$format(copyText)}</button>
                </Clipboard>
                <Prism showLineNumbers={true} code={fusionJS} />
              </div>
            </ShowMore>
          </SwitchField>

          <SwitchField
            id="hideipsinfeeds"
            label={$format('label.INCLUDE_IPS_IN_FEEDS')}
            tooltip={$format('label.TOOLTIP.INCLUDE_IPS_IN_FEEDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.hideipsinfeeds}
            inverted
            {onUpdate}
            {showActions}
          />

          <SwitchField
            id="disableenrichment"
            label={$format('label.CONTACT_ENRICHMENT')}
            tooltip={$format('label.TOOLTIP.CONTACT_ENRICHMENT')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.disableenrichment}
            inverted
            {onUpdate}
            {showActions}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Permission {allowed} id="general-personas">
    <ScrollableSection id="general-personas">
      <h1 class="mb-3">{$format('label.PERSONAS')}</h1>
      <Card>
        <CardBody class="p-4 pt-1">
          <MultiSelectField
            id="personas-roles"
            label={$format('label.BUYER_ROLES')}
            bind:selected={selectedRoles}
            bind:options={roles}
            {onUpdate}
            {showActions}
          />

          <MultiSelectField
            id="personas-departments"
            label={$format('label.BUYER_DEPARTMENTS')}
            bind:selected={selectedDepartments}
            bind:options={departments}
            {onUpdate}
            {showActions}
          />

          <MultiSelectField
            id="personas-executives"
            label={$format('label.EXECUTIVE_KEYWORDS')}
            bind:selected={selectedExecutives}
            bind:options={executives}
            {onUpdate}
            {showActions}
          />

          <MultiSelectField
            id="personas-influencers"
            label={$format('label.INFLUENCER_KEYWORDS')}
            bind:selected={selectedInfluencers}
            bind:options={influencers}
            {onUpdate}
            {showActions}
          />

          <MultiSelectField
            id="personas-excluded"
            label={$format('label.EXCLUDED_KEYWORDS')}
            bind:selected={selectedExcluded}
            bind:options={excluded}
            {onUpdate}
            {showActions}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <!-- <ScrollableSection id="general-fusion">
    <h1>Fusion</h1>

    <SwitchField
      id="websitetrackingenabled"
      label="Website Tracking <span class='beta-badge'>Beta</span>"
      tooltip="Enable website visitor tracking and alerts."
      valueOn={$format(enabledValue)}
      valueOff={$format(disabledValue)}
      bind:checked={settings.websitetrackingenabled}
      {onUpdate}
    />

    <SwitchField
      id="fusionenabled"
      label="Website Site Playback <span class='beta-badge'>Beta</span>"
      tooltip="Record the users interactions on your website."
      valueOn={$format(enabledValue)}
      valueOff={$format(disabledValue)}
      bind:checked={settings.fusionenabled}
      {onUpdate}
    >
      <ShowMore collapsedText="Show snippet" expandedText="Hide snippet">
        <div class="snippet">
          <Clipboard text={fusionJS} let:copy on:copy={onCopy}>
            <button on:click={copy} class="copy-to-clipboard">{copyText}</button>
          </Clipboard>
          <Prism showLineNumbers={true} code={fusionJS} />
        </div>
      </ShowMore>
    </SwitchField>

    <SwitchField
      id="docfusionenabled"
      label="Document Playback <span class='beta-badge'>Beta</span>"
      tooltip="Record the users interactions with your documents."
      valueOn={$format(enabledValue)}
      valueOff={$format(disabledValue)}
      bind:checked={settings.docfusionenabled}
      {onUpdate}
    />

    <TextField
      id="fusiontype"
      label="Site Fusion Type"
      tooltip="sendergen or inspectlet"
      bind:value={settings.fusiontype}
      {onUpdate}
    />

    <TextField
      id="fusionuser"
      label="Site Fusion User"
      tooltip="Inspectlet API Username. Contact support@sendergen.com"
      bind:value={settings.fusionuser}
      {onUpdate}
    />

    <TextField
      id="fusiontoken"
      label="Site Fusion Token"
      tooltip="Inspectlet API Token."
      bind:value={settings.fusiontoken}
      {onUpdate}
    />

    <KeyValueField
      id="fusionparams"
      label="Site Fusion Domains"
      tooltip="Inspectlet domain names and Website IDs."
      addButtonLabel="Add Domain"
      keyColumnLabel="Domain"
      keyValidation={isValidDomain}
      keyValidationError="Enter a valid domain."
      valueColumnLabel="Website ID"
      valueValidation={isNumerical}
      valueValidationError="Only numbers 0-9 are allowed."
      bind:values={settings.fusionparams}
      {onUpdate}
    />

    <TextField
      id="fusiontoken"
      label="Site Fusion CNAME Website ID"
      tooltip="Inspectlet CNAME Website ID"
      bind:value={settings.fusioncnamewid}
      {onUpdate}
    />
  </ScrollableSection> -->

  <Permission {allowed} id="general-users">
    <ScrollableSection id="general-users">
      <h1 class="mb-3">{$format('label.USERS')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <SwitchField
            id="enabledefaultparams"
            label={$format('label.USE_DEFAULT_FIELDS')}
            tooltip={$format('label.TOOLTIP.USE_DEFAULT_FIELDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enabledefaultparams}
            {onUpdate}
            {showActions}
          >
            {#if settings.enabledefaultparams}
              <div class="user-settings">
                <ParamsField
                  id="defaultparams"
                  label={$format('label.DEFAULT_FIELDS')}
                  tooltip={$format('label.TOOLTIP.DEFAULT_FIELDS')}
                  value={settings.defaultparams}
                  {onUpdate}
                />
              </div>
            {/if}
          </SwitchField>

          <SwitchField
            id="enabledefaultusersettings"
            label={$format('label.USE_DEFAULT_SETTINGS')}
            tooltip={$format('label.TOOLTIP.USE_DEFAULT_SETTINGS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enabledefaultusersettings}
            {onUpdate}
            {showActions}
          >
            {#if settings.enabledefaultusersettings}
              <div class="user-settings">
                <SwitchField
                  id="defaultusersettings-enablesales"
                  label={$format('label.SALES_PRODUCTS')}
                  tooltip={$format('label.TOOLTIP.SALES_PRODUCTS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.enablesales}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-autotrackemails"
                  label={$format('label.AUTO_TRACK_EMAILS')}
                  tooltip={$format('label.TOOLTIP.AUTO_TRACK_EMAILS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.autotrackemails}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-autotracklinks"
                  label={$format('label.AUTO_TRACK_LINKS')}
                  tooltip={$format('label.TOOLTIP.AUTO_TRACK_LINKS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.autotracklinks}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-autotrackdocs"
                  label={$format('label.AUTO_TRACK_ATTACHMENTS')}
                  tooltip={$format('label.TOOLTIP.AUTO_TRACK_ATTACHMENTS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.autotrackdocs}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-enabletiming"
                  label={$format('label.DWELL_TIME_BETA')}
                  tooltip={$format('label.TOOLTIP.DWELL_TIME_BETA')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.enabletiming}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-enableatticon"
                  label={$format('label.ATTACHMENT_ICON')}
                  tooltip={$format('label.TOOLTIP.ATTACHMENT_ICON')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.enableatticon}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-keepattachments"
                  label={$format('label.KEEP_ORIGINAL_ATTACHMENTS')}
                  tooltip={$format('label.TOOLTIP.KEEP_ORIGINAL_ATTACHMENTS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.keepattachments}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-attsattop"
                  label={$format('label.ATTACHMENTS_AT_TOP')}
                  tooltip={$format('label.TOOLTIP.ATTACHMENTS_AT_TOP')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.attsattop}
                  {onUpdate}
                  {showActions}
                />

                <TextField
                  id="defaultusersettings-autobcc"
                  label={$format('label.AUTO_BCC')}
                  tooltip={$format('label.TOOLTIP.AUTO_BCC')}
                  validationError="Please enter a valid email address."
                  validation={isValidEmail}
                  bind:value={settings.defaultusersettings.autobcc}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-trackautobcc"
                  label={$format('label.TRACK_AUTO_BCC')}
                  tooltip={$format('label.TOOLTIP.TRACK_AUTO_BCC')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.trackautobcc}
                  {onUpdate}
                  {showActions}
                />

                <TextField
                  id="defaultusersettings-donottrack"
                  label={$format('label.DO_NOT_TRACK_LIST')}
                  tooltip={$format('label.TOOLTIP.DO_NOT_TRACK_LIST')}
                  bind:value={settings.defaultusersettings.donottrack}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-disablefeed"
                  label={$format('label.SMART_ACTIVITY_FEED')}
                  tooltip={$format('label.TOOLTIP.SMART_ACTIVITY_FEED')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  inverted
                  bind:checked={settings.defaultusersettings.disablefeed}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-trackautoforwards"
                  label={$format('label.TRACK_AUTO_FORWARDED_EMAILS')}
                  tooltip={$format('label.TOOLTIP.TRACK_AUTO_FORWARDED_EMAILS')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.trackautoforwards}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-disablesplitsend"
                  label={$format('label.MULTI_RECIPIENT_TRACKING')}
                  tooltip={$format('label.TOOLTIP.MULTI_RECIPIENT_TRACKING')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  inverted
                  bind:checked={settings.defaultusersettings.disablesplitsend}
                  {onUpdate}
                  {showActions}
                />

                <SwitchField
                  id="defaultusersettings-usequickhash"
                  label={$format('label.USE_##_SHORTCUT')}
                  tooltip={$format('label.TOOLTIP.USE_##_SHORTCUT')}
                  valueOn={$format(enabledValue)}
                  valueOff={$format(disabledValue)}
                  bind:checked={settings.defaultusersettings.usequickhash}
                  {onUpdate}
                  {showActions}
                />
              </div>
            {/if}
          </SwitchField>

          <SwitchField
            id="enabledefaultnotificationsettings"
            label={$format('label.USE_DEFAULT_NOTIFICATION_SETTINGS')}
            tooltip={$format('label.TOOLTIP.USE_DEFAULT_NOTIFICATION_SETTINGS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enabledefaultnotificationsettings}
            {onUpdate}
            {showActions}
          >
            {#if settings.enabledefaultnotificationsettings}
              <div class="user-settings">
                <UserNotifications bind:settings={settings.defaultnotificationsettings} {onUpdate} isUser={false} />
              </div>
            {/if}
          </SwitchField>
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>

  <Permission {allowed} id="general-audit-history">
    <ScrollableSection id="general-audit-history">
      <h1 class="mb-3">{$format('label.AUDIT_HISTORY')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <SwitchField
            id="auditconfig-enabled"
            label={$format('label.ENABLE_THIRD_PARTY_PUSH')}
            tooltip={$format('label.TOOLTIP.ENABLE_THIRD_PARTY_PUSH')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.auditconfig.enabled}
            {onUpdate}
            {showActions}
          />

          {#if settings.auditconfig.enabled}
            <TextField
              id="auditconfig-endpoint"
              label={$format('label.POST_ENDPOINT')}
              tooltip={$format('label.TOOLTIP.POST_ENDPOINT')}
              validationError={$format('label.VALIDATION_ERROR_NO_VALID_URL')}
              validation={isValidUrl}
              bind:value={settings.auditconfig.endpoint}
              {onUpdate}
              {showActions}
            />

            <PasswordField
              id="auditconfig-token"
              label={$format('label.AUTHORIZATION_TOKEN')}
              tooltip={$format('label.TOOLTIP.AUTHORIZATION_TOKEN')}
              bind:value={settings.auditconfig.token}
              {onUpdate}
              {showActions}
            />
          {/if}
        </CardBody>
      </Card>
    </ScrollableSection>
  </Permission>
</div>

<style lang="scss" src="./general.scss"></style>
